/**
 * Lo que convierte un árbol guardado en un árbol dibujable.
 *
 * Una página no se basta a sí misma: sus secciones reutilizables son cáscaras
 * vacías —sólo un `componentId`— y sus textos enlazados no guardan texto, sólo
 * a dónde mirar. Las dos cosas se resuelven contra `ProjectContent`, que es del
 * sitio y no de la página.
 *
 * Sin esto, una cabecera compartida se dibuja como un `<div>` sin hijos: no
 * falla nada, simplemente no se ve. Es exactamente lo que le pasaba a la vista
 * previa de las copias, que nunca pasaba este contenido.
 *
 * Portado del editor (`lib/components.ts` y `CanvasRenderer`) en vez de
 * importado: el editor es otra aplicación y su renderizador arrastra el lienzo
 * entero. Lo que se copia es el ALGORITMO, que es pequeño y estable; si algún
 * día el render se extrae a un paquete compartido, este archivo desaparece.
 */
import type { CmsCollection, ContentBinding, ElementNode, ProjectComponent, ProjectContent } from './types'

/** El id con el que una colección se refiere al catálogo de colecciones. */
const CMS_COLLECTIONS_SOURCE = '__collections__'

function projectComponents(content: ProjectContent | undefined): ProjectComponent[] {
  return content?.components ?? []
}

/**
 * Las instancias, con el contenido de su original.
 *
 * Se conserva el nodo de la instancia —sus estilos ganan, que es lo que permite
 * que la misma sección lleve otro margen en una página— y lo que viene del
 * original son los hijos.
 */
export function resolveComponentInstances(
  nodes: ElementNode[],
  content: ProjectContent | undefined,
  /** Guarda contra un componente que se contiene a sí mismo. */
  seen: readonly string[] = [],
): ElementNode[] {
  const components = projectComponents(content)
  if (components.length === 0) return nodes

  const resolve = (node: ElementNode): ElementNode => {
    if (!node.componentId) {
      const children = node.children.length ? node.children.map(resolve) : node.children
      return children === node.children ? node : { ...node, children }
    }

    /* Un componente dentro de sí mismo se dibujaría hasta agotar la pila. Se
       corta y se deja vacío: es visible y arreglable; colgarse no lo es. */
    if (seen.includes(node.componentId)) return { ...node, children: [] }

    const master = components.find((item) => item.id === node.componentId)
    /* Sin original —borrado, o una copia de antes de que existiera— la
       instancia se queda con lo que tuviera dentro en vez de desaparecer. */
    if (!master) return node

    return {
      ...node,
      children: resolveComponentInstances(master.node.children, content, [...seen, node.componentId]),
    }
  }

  return nodes.map(resolve)
}

/** Lee `a.b.c` sobre un objeto cualquiera, sin reventar por el camino. */
function readPath(source: unknown, path: string): unknown {
  return path.split('.').reduce<unknown>((value, key) => {
    if (value == null || typeof value !== 'object') return undefined
    return (value as Record<string, unknown>)[key]
  }, source)
}

/** Lo que puede empezar una dirección de imagen. */
function looksLikeUrl(value: string): boolean {
  const url = value.trim()
  if (!url) return false
  return url.startsWith('/') || url.startsWith('data:') || url.startsWith('blob:') || /^https?:\/\//i.test(url)
}

/**
 * La primera imagen de un campo que puede traer varias.
 *
 * El formato nuevo es JSON; el viejo, urls separadas por comas. Se parte por
 * comas SÓLO si cada trozo parece una dirección por sí mismo: hay urls que
 * llevan comas dentro (las transformaciones de Cloudinary) y partirlas a ciegas
 * las rompe.
 */
export function firstImageUrl(raw: string | undefined): string {
  const value = (raw ?? '').trim()
  if (!value) return ''

  if (value.startsWith('[')) {
    try {
      const parsed: unknown = JSON.parse(value)
      if (Array.isArray(parsed)) {
        const first = parsed.find((item): item is string => typeof item === 'string' && item.trim() !== '')
        return first ? first.trim() : ''
      }
    } catch {
      /* JSON roto: se sigue por el camino de siempre en vez de perder el valor. */
    }
  }

  if (!value.includes(',')) return value

  const parts = value.split(',').map((part) => part.trim()).filter(Boolean)
  return parts.every(looksLikeUrl) ? (parts[0] ?? '') : value
}

function collectionField(collection: CmsCollection | undefined, binding: ContentBinding): string | undefined {
  if (!collection) return undefined
  if (binding.field === '__name') return collection.name
  if (binding.field === '__description') return collection.description ?? ''
  if (binding.field === '__image') return collection.image ?? ''
  if (binding.field === '__icon') return collection.icon ?? ''
  return undefined
}

/**
 * El valor de un elemento enlazado, o `undefined` si no lo hay.
 *
 * Aquí se dibuja lo PUBLICADO cuando existe (`publishedValues`), que es lo que
 * el visitante vería: una copia se mira para compararla con el sitio, no con el
 * borrador que hubiera abierto en el editor.
 */
export function resolveBindingValue(
  node: ElementNode,
  projectContent: ProjectContent | undefined,
): string | undefined {
  const binding = node.contentBinding
  if (!binding || binding.source !== 'projectContent' || !projectContent) return undefined

  const collectionId = binding.collection || ''

  // El catálogo de colecciones: la «ficha» es una colección entera.
  if (collectionId === CMS_COLLECTIONS_SOURCE) {
    return collectionField(projectContent.collections?.find((c) => c.id === binding.itemId), binding)
  }

  const dynamic = projectContent.collections?.find((c) => c.id === collectionId)
  if (dynamic) {
    const singleton = collectionField(dynamic, binding)
    if (singleton !== undefined) return singleton
    const item = binding.itemId ? dynamic.items.find((it) => it.id === binding.itemId) : dynamic.items[0]
    const values = item?.publishedValues ?? item?.values
    const value = values?.[binding.field]
    return typeof value === 'string' ? value : undefined
  }

  /* Los singulares del sitio: negocio, contacto y navegación. Es de donde salen
     el nombre de la marca, el teléfono del banner y las etiquetas del menú. */
  const source = binding.collection === 'navigation'
    ? binding.itemId
      ? projectContent.navigation?.items?.find((item) => item.id === binding.itemId)
      : projectContent.navigation
    : (projectContent as unknown as Record<string, unknown>)[binding.collection]

  const value = readPath(source, binding.field)
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  return undefined
}
